// ADR-0051 / V101-1-2: the registry of every connector kind. Grouped by
// family; one const per kind. Scopes here are the source the adapters must
// match (a Worker-side conformance test pins them until V101-1-4 makes the
// adapters import from here).
import { POSTURE_CHECKS, postureCheck } from '../data/posture-checks.js';
import { aiProviderCheck } from './ai-collector.js';
import { registerManifestRegistry } from './manifest-override.js';
import { K8S_RBAC_SCOPES } from './kubernetes-rbac.js';
import { SNOWFLAKE_HINTS } from './hints/snowflake.js';
import { CLOUDFLARE_HINTS } from './hints/cloudflare.js';
/**
 * G-1a: keeps each entry's `kind` literal so ConnectorKind is derived from this
 * registry (no hand-kept union).
 */
function defineManifest(m) {
    return m;
}
const NEVER = ['secrets', 'raw_pii'];
const NEVER_BODIES = ['secrets', 'raw_pii', 'prompt_body', 'response_body'];
/** G-1l: cloud, SaaS and data-store kinds never read the customer's business records. */
const NEVER_DATA = ['secrets', 'raw_pii', 'business_data'];
const NEVER_BODIES_DATA = ['secrets', 'raw_pii', 'prompt_body', 'response_body', 'business_data'];
// ── Connect-form building blocks (G-1e) ─────────────────────────────────
const GUID_HINT = 'Format: 00000000-0000-0000-0000-000000000000';
const link = (href, text) => ({ link: { href, text } });
const secret = (key, label, extra = {}) => ({ key, label, store: 'credential', ...extra });
const ADMIN_KEY = (prefix, pattern, vendor, href) => secret('admin_api_key', 'Admin API key', {
    validate: { regex: pattern },
    message: `Use an Admin API key (starts with ${prefix}).`,
    hint: ['Starts with ', { code: prefix }, ' · ', link(href, 'Create an admin key')],
});
/** Datadog sites (the manifest's fixed api.<site> hosts); app_host serves the web UI. */
const DATADOG_SITE_OPTIONS = [
    { value: 'datadoghq.com', label: 'US1 · datadoghq.com', vars: { app_host: 'app.datadoghq.com' } },
    { value: 'us3.datadoghq.com', label: 'US3 · us3.datadoghq.com', vars: { app_host: 'us3.datadoghq.com' } },
    { value: 'us5.datadoghq.com', label: 'US5 · us5.datadoghq.com', vars: { app_host: 'us5.datadoghq.com' } },
    { value: 'ap1.datadoghq.com', label: 'AP1 · ap1.datadoghq.com', vars: { app_host: 'ap1.datadoghq.com' } },
    { value: 'ap2.datadoghq.com', label: 'AP2 · ap2.datadoghq.com', vars: { app_host: 'ap2.datadoghq.com' } },
    { value: 'datadoghq.eu', label: 'EU1 · datadoghq.eu', vars: { app_host: 'app.datadoghq.eu' } },
    { value: 'ddog-gov.com', label: 'US1-FED · ddog-gov.com', vars: { app_host: 'app.ddog-gov.com' } },
];
const DAILY = { defaultCadence: 'P1D', minCadence: 'PT1H' };
/**
 * V102-9-1 / ADR-0061: sovereign-cloud endpoints for Azure. The commercial
 * and USGov pairs use different ARM and login hosts; GCC High (M365 variant)
 * rides on the same USGov endpoints. The `cloud` form option drives host
 * selection at run time; both pairs are listed in `hosts.fixed` so the SSRF
 * guard accepts whichever endpoint the collector picks.
 */
const AZURE_CLOUD_OPTIONS = [
    { value: 'commercial', label: 'Azure Commercial', vars: { arm: 'management.azure.com', login: 'login.microsoftonline.com' } },
    { value: 'usgov', label: 'Azure Government', vars: { arm: 'management.usgovcloudapi.net', login: 'login.microsoftonline.us' } },
    { value: 'gcc_high', label: 'Azure GCC High', vars: { arm: 'management.usgovcloudapi.net', login: 'login.microsoftonline.us' } },
];
const OKTA_HOSTS = ['*.okta.com', '*.oktapreview.com', '*.okta-emea.com', '*.okta-gov.com'];
const GRAPH_HOSTS = ['graph.microsoft.com', 'login.microsoftonline.com'];
// V101-3-4/3-5: AI checks the cloud read-back roles also emit.
const AWS_AI_CHECKS = ['ai_usage_logging_enabled', 'ai_guardrails_configured', 'ai_prompt_attack_filter', 'ai_rag_sources_governed', 'agent_memory_retention', 'ai_model_inventory', 'ai_model_cards', 'ai_model_monitoring', 'ai_service_authn_required'];
const GCP_AI_CHECKS = ['ai_usage_logging_enabled', 'ai_model_inventory', 'ai_model_monitoring', 'ai_guardrails_configured', 'ai_prompt_attack_filter', 'vector_store_encrypted', 'ai_threat_detection_active'];
const AWS_SCOPES = ['aws-policy:SecurityAudit', 'aws-policy:AmazonBedrockReadOnly', 'aws-policy:AmazonSageMakerReadOnly',
    // V113-2-2 / ADR-0102 §6 (manifest v2): Bedrock CloudWatch metrics (aggregates only; not CloudWatchReadOnlyAccess, which reads logs).
    'aws-action:cloudwatch:ListMetrics', 'aws-action:cloudwatch:GetMetricData'];
const GCP_ROLES = ['roles/iam.securityReviewer', 'roles/cloudasset.viewer', 'roles/securitycenter.findingsViewer', 'roles/logging.viewer', 'roles/orgpolicy.policyViewer', 'roles/aiplatform.viewer', 'roles/modelarmor.viewer', 'roles/securitycentermanagement.viewer',
    // v10.9 LM-26 (R20): Privileged Access Manager entitlements + grants, get / list only.
    'roles/privilegedaccessmanager.viewer',
    // V113-2-2 / ADR-0102 §6 (manifest v2): Vertex prediction / error / drift time series (monitoring.timeSeries.list).
    'roles/monitoring.viewer'];
// ── Identity ────────────────────────────────────────────────────────────
const oktaPosture = defineManifest({
    kind: 'okta_posture', manifestVersion: 1, lifecycle: 'active', family: 'idp',
    provider: { key: 'okta', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['okta.policies.read', 'okta.users.read', 'okta.roles.read', 'okta.logs.read', 'okta.authenticators.read'] }],
    hosts: { customerSupplied: { field: 'org_url', patterns: OKTA_HOSTS } },
    outputs: { checks: ['idp_mfa_enforced', 'idp_phishing_resistant_auth', 'idp_deprovisioning_timely', 'idp_admins_minimized'], populations: ['workforce_users', 'admins'] },
    captureShapes: ['posture', 'population'],
    dataBoundary: { collects: ['config', 'identities_hashed'], neverCollects: NEVER },
    events: [{
            verifier: 'okta_event_hook',
            eventTypes: ['policy.lifecycle.*', 'policy.rule.*', 'system.mfa.factor.*', 'user.mfa.factor.deactivate', 'group.privilege.*', 'user.account.privilege.*'],
            triggersChecks: ['idp_mfa_enforced', 'idp_phishing_resistant_auth', 'idp_admins_minimized'],
        }],
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 600, requestsPerMinute: 300 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Okta', group: 'Identity provider' }, vendorSlug: 'okta',
    blueprint: { slots: ['identity'], claims: ['SSO/OIDC via Okta'] },
});
const entraPosture = defineManifest({
    kind: 'entra_posture', manifestVersion: 1, lifecycle: 'active', family: 'idp',
    provider: { key: 'microsoft-entra', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'graph_read', scopes: ['Policy.Read.All', 'User.Read.All', 'Directory.Read.All', 'RoleEligibilitySchedule.Read.Directory', 'AuditLog.Read.All', 'UserAuthenticationMethod.Read.All'] }],
    hosts: { fixed: GRAPH_HOSTS },
    outputs: { checks: ['idp_mfa_enforced', 'idp_phishing_resistant_auth', 'idp_deprovisioning_timely', 'idp_admins_minimized', 'idp_privileged_access_jit'], populations: ['workforce_users', 'admins'] },
    captureShapes: ['posture', 'population'],
    dataBoundary: { collects: ['config', 'identities_hashed'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 800, requestsPerMinute: 600 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Microsoft Entra ID', group: 'Identity provider', target: { field: 'tenant_id', display: 'tail4', noun: 'Tenant' } }, vendorSlug: 'entra',
    blueprint: { slots: ['identity'], claims: ['SSO/SAML via Microsoft Entra ID'] },
});
const googleWorkspace = defineManifest({
    kind: 'google_workspace', manifestVersion: 1, lifecycle: 'active', family: 'idp',
    provider: { key: 'google-workspace', channel: 'direct' },
    auth: [
        { kind: 'gcp_wif', lintProfile: 'google_readonly', scopes: ['https://www.googleapis.com/auth/admin.directory.user.readonly', 'https://www.googleapis.com/auth/admin.reports.audit.readonly', 'https://www.googleapis.com/auth/cloud-identity.policies.readonly'] },
        { kind: 'gcp_sa_key', lintProfile: 'google_readonly', weaker: true, scopes: ['https://www.googleapis.com/auth/admin.directory.user.readonly', 'https://www.googleapis.com/auth/admin.reports.audit.readonly', 'https://www.googleapis.com/auth/cloud-identity.policies.readonly'] },
    ],
    hosts: { fixed: ['admin.googleapis.com', 'cloudidentity.googleapis.com', 'oauth2.googleapis.com', 'iamcredentials.googleapis.com', 'sts.googleapis.com'] },
    outputs: { checks: ['idp_mfa_enforced', 'idp_phishing_resistant_auth', 'idp_admins_minimized', 'saas_external_sharing_restricted', 'saas_audit_logging_enabled'], populations: ['workforce_users'] },
    captureShapes: ['posture', 'population'],
    dataBoundary: { collects: ['config', 'identities_hashed'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 600, requestsPerMinute: 300 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Google Workspace', group: 'Productivity suite' }, vendorSlug: 'google-workspace',
    blueprint: { slots: ['identity'], claims: ['Identity via Google Workspace'] },
});
const microsoft365 = defineManifest({
    kind: 'microsoft_365', manifestVersion: 1, lifecycle: 'active', family: 'idp',
    provider: { key: 'microsoft-365', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'graph_read', scopes: ['User.Read.All', 'Policy.Read.All', 'RoleManagement.Read.Directory', 'Reports.Read.All', 'SharePointTenantSettings.Read.All', 'AuditLog.Read.All'] }],
    hosts: { fixed: GRAPH_HOSTS },
    outputs: { checks: ['idp_mfa_enforced', 'idp_phishing_resistant_auth', 'idp_admins_minimized', 'saas_external_sharing_restricted', 'saas_audit_logging_enabled'], populations: ['workforce_users'] },
    captureShapes: ['posture', 'population'],
    dataBoundary: { collects: ['config', 'identities_hashed'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 800, requestsPerMinute: 600 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Microsoft 365', group: 'Productivity suite', target: { field: 'tenant_id', display: 'tail4', noun: 'Tenant' } }, vendorSlug: 'microsoft-365',
    blueprint: { slots: ['identity'], claims: ['Identity via Microsoft 365'] },
});
// Shadow-AI discovery from OAuth grants (V101-0-3 wires creation + schedule).
const oktaGrants = defineManifest({
    kind: 'okta', manifestVersion: 1, lifecycle: 'active', family: 'idp',
    provider: { key: 'okta', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['okta.apps.read', 'okta.logs.read'] }],
    hosts: { customerSupplied: { field: 'org_url', patterns: OKTA_HOSTS } },
    outputs: { checks: [], rollups: ['discovery_signal_aggregates'], inventory: ['discovered_tools'], discoverySignals: ['oauth_grant', 'idp_signin'] },
    captureShapes: ['bulk'],
    dataBoundary: { collects: ['metadata', 'identities_hashed'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 1500, requestsPerMinute: 300 },
    runner: 'idp_grants', managedBy: 'posture_connectors',
    ui: { label: 'Okta · AI app discovery', group: 'AI app discovery' }, vendorSlug: 'okta',
    blueprint: { slots: ['identity'], claims: ['SSO/OIDC via Okta'] },
});
const entraGrants = defineManifest({
    ...oktaGrants,
    kind: 'entra',
    provider: { key: 'microsoft-entra', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'graph_read', scopes: ['Application.Read.All', 'Directory.Read.All'] }],
    hosts: { fixed: GRAPH_HOSTS },
    ui: { label: 'Microsoft Entra · AI app discovery', group: 'AI app discovery', target: { field: 'tenant_id', display: 'tail4', noun: 'Tenant' } }, vendorSlug: 'entra',
    blueprint: { slots: ['identity'], claims: ['SSO/SAML via Microsoft Entra ID'] },
});
// ── Cloud ───────────────────────────────────────────────────────────────
// V104-C-4 (review I-1): the hosts the AWS collectors call, never `*.amazonaws.com`.
// `{region}` is one region label from config.regions (iam.us-gov is GovCloud's IAM).
const AWS_STS_HOSTS = ['sts.amazonaws.com', 'sts.{region}.amazonaws.com'];
const AWS_REGIONAL_SERVICES = [
    'cloudtrail', 'config', 'ec2', 'securityhub', 'inspector2', 'backup', // posture read-back
    'rds', 'lambda', 'elasticloadbalancing', // resource inventory (ADR-0066 A3)
    'bedrock', 'bedrock-agent', 'bedrock-agentcore-control', 'api.sagemaker', // AI read-back (V101-3-4)
    'monitoring', // CloudWatch metrics (V113-2-2)
];
const AWS_HOSTS = [
    ...AWS_STS_HOSTS,
    'iam.amazonaws.com', 'iam.us-gov.amazonaws.com',
    ...AWS_REGIONAL_SERVICES.map((svc) => `${svc}.{region}.amazonaws.com`),
    // S3 bucket listing (inventory) and the Pinecone audit-log bucket (V101-3-6, virtual-hosted per bucket).
    's3.amazonaws.com', 's3.{region}.amazonaws.com', '*.s3.{region}.amazonaws.com',
    // BL-24 (C-7 L-2): called whatever config.regions says. GetBucketLocation for the Pinecone
    // audit bucket always goes to us-east-1; GovCloud's bucket listing goes to us-gov-west-1.
    's3.us-east-1.amazonaws.com', '*.s3.us-east-1.amazonaws.com', 's3.us-gov-west-1.amazonaws.com',
];
const aws = defineManifest({
    kind: 'aws', manifestVersion: 2, lifecycle: 'active', family: 'cloud',
    scopeChanges: [{ version: 2, added: ['aws-action:cloudwatch:ListMetrics', 'aws-action:cloudwatch:GetMetricData'], reason: 'V113-2-2: Bedrock invocation and guardrail metrics as daily rollups (ADR-0102)' }],
    provider: { key: 'aws', channel: 'direct', channels: ['bedrock'] },
    auth: [
        // ADR-0053 §1: keyless (GAC Pro OIDC issuer) is the default; ExternalId + GAC Pro keys is the weaker legacy path.
        // V101-3-4: Bedrock/SageMaker read-back uses the same role (AI checks recorded only when AI resources exist).
        { kind: 'aws_oidc', lintProfile: 'iam_policy', scopes: AWS_SCOPES },
        { kind: 'aws_role', lintProfile: 'iam_policy', scopes: AWS_SCOPES, weaker: true },
    ],
    // Regional service endpoints (<service>.<region>.amazonaws.com) are derived from config.regions.
    hosts: { fixed: AWS_HOSTS },
    outputs: { checks: ['cloud_audit_trail_centralized', 'cloud_config_compliant', 'cloud_benchmarks_passing', 'cloud_network_restricted', 'cloud_asset_inventory_recording', 'cloud_encryption_at_rest', 'cloud_service_credentials_rotated', 'cloud_backups_configured', 'cloud_vuln_scanning_active', 'vuln_remediation_within_timeframe',
            ...AWS_AI_CHECKS,
            // V101-3-6 (D-CI-35): SaaS audit logs delivered to this account's S3 (Pinecone), object list only.
            'saas_audit_logging_enabled'],
        // V103-4-3 / ADR-0066 A3: read-only resource listing (EC2, RDS, Lambda, ELB, S3, EKS) under SecurityAudit.
        inventory: ['resources'],
        // V113-2-2 (ADR-0102 §1): Bedrock CloudWatch metrics as invocation / filter rollups.
        rollups: ['ai_usage_rollups'] },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: NEVER_DATA },
    events: [{
            verifier: 'aws_eventbridge',
            eventTypes: ['aws.config:*', 'aws.securityhub:*', 'aws.guardduty:*', 'aws.inspector2:*'],
            triggersChecks: ['cloud_config_compliant', 'cloud_benchmarks_passing', 'cloud_vuln_scanning_active'],
        }],
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 1500, requestsPerMinute: 600 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'AWS', group: 'Cloud account', target: { field: 'role_arn', display: 'raw' } }, vendorSlug: 'aws',
});
const gcp = defineManifest({
    kind: 'gcp', manifestVersion: 2, lifecycle: 'active', family: 'cloud',
    scopeChanges: [{ version: 2, added: ['roles/monitoring.viewer'], reason: 'V113-2-2: Vertex prediction, error and drift metrics as daily rollups (ADR-0102)' }],
    provider: { key: 'gcp', channel: 'direct', channels: ['vertex'] },
    auth: [
        { kind: 'gcp_wif', lintProfile: 'gcp_roles', scopes: GCP_ROLES },
        { kind: 'gcp_sa_key', lintProfile: 'gcp_roles', weaker: true, scopes: GCP_ROLES },
    ],
    // V101-3-5: regional Vertex ({loc}-aiplatform.googleapis.com) and Model Armor (modelarmor.{loc}.rep.googleapis.com;
    // V104-B-2: modelarmor.googleapis.com for the global location);
    // `{region}` is one location from config.ai_locations (V104-C-4: no `*.googleapis.com`).
    hosts: { fixed: ['aiplatform.googleapis.com', '{region}-aiplatform.googleapis.com', 'modelarmor.{region}.rep.googleapis.com', 'modelarmor.googleapis.com', 'securitycentermanagement.googleapis.com', 'cloudasset.googleapis.com', 'cloudresourcemanager.googleapis.com', 'orgpolicy.googleapis.com', 'securitycenter.googleapis.com', 'logging.googleapis.com', 'sts.googleapis.com', 'iamcredentials.googleapis.com', 'oauth2.googleapis.com',
            // v10.9 LM-26 (R20): Privileged Access Manager v1 (entitlements.list, grants.list; GET only).
            'privilegedaccessmanager.googleapis.com',
            // V113-2-2 (ADR-0102 §6): Cloud Monitoring timeSeries.list (GET only).
            'monitoring.googleapis.com'] },
    outputs: { checks: ['cloud_audit_trail_centralized', 'cloud_config_compliant', 'cloud_benchmarks_passing', 'cloud_network_restricted', 'cloud_asset_inventory_recording', 'cloud_service_credentials_rotated', 'cloud_public_access_blocked', 'cloud_privileged_bindings_minimized',
            // V109-3-3 (slice R07): read since v9.7, judged from v10.9 (same getIamPolicy call, no new role).
            'cloud_data_access_audit_enabled',
            // v10.9 LM-26 (R20): privileged roles granted just in time (getIamPolicy + Privileged Access Manager).
            'cloud_privileged_access_jit',
            ...GCP_AI_CHECKS],
        // V103-4-3 / ADR-0066 A3: Cloud Asset searchAllResources (roles/cloudasset.viewer, already held).
        inventory: ['resources'],
        // V113-2-2 (ADR-0102 §1): Vertex metrics as invocation / drift rollups.
        rollups: ['ai_usage_rollups'] },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: NEVER_DATA },
    events: [{
            verifier: 'gcp_pubsub_oidc',
            eventTypes: ['*'],
            triggersChecks: ['cloud_public_access_blocked', 'cloud_config_compliant', 'cloud_benchmarks_passing', 'cloud_privileged_bindings_minimized'],
        }],
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 1500, requestsPerMinute: 600 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Google Cloud', group: 'Cloud account', target: { field: 'scope', display: 'raw' } }, vendorSlug: 'gcp',
});
// ── Source control ──────────────────────────────────────────────────────
const github = defineManifest({
    kind: 'github', manifestVersion: 1, lifecycle: 'active', family: 'scm',
    provider: { key: 'github', channel: 'direct' },
    auth: [
        { kind: 'oauth', lintProfile: 'github_permissions', scopes: ['contents:read', 'metadata:read'] },
        { kind: 'pat', lintProfile: 'generic', weaker: true, scopes: ['repo:read'] },
    ],
    hosts: { fixed: ['api.github.com'] },
    outputs: {
        checks: ['github_branch_protection', 'github_code_review_required', 'github_secret_scanning', 'github_codeowners', 'branch_protection_enabled', 'required_reviews_gte_1', 'secret_scanning_enabled'],
        inventory: ['components'],
    },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER },
    schedule: DAILY,
    events: [{ verifier: 'github_hmac', eventTypes: ['branch_protection_rule', 'repository', 'repository_ruleset'], triggersChecks: ['github_branch_protection', 'github_code_review_required'] }],
    runnableIn: ['worker'],
    limits: { maxRequests: 300, requestsPerMinute: 300 },
    runner: 'github', managedBy: 'connectors',
    ui: { label: 'GitHub', group: 'Source control' }, vendorSlug: 'github',
    blueprint: { slots: ['source_control', 'ci_cd', 'human_review'], claims: ['Git-based version control via GitHub', 'CI/CD via GitHub Actions', 'PR review workflows'] },
});
// ── Vulnerability ───────────────────────────────────────────────────────
const tenable = defineManifest({
    kind: 'tenable', manifestVersion: 1, lifecycle: 'active', family: 'vuln',
    provider: { key: 'tenable', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['tenable:vulns.export.read'] }],
    hosts: { fixed: ['cloud.tenable.com', 'fedcloud.tenable.com'] },
    outputs: { checks: ['vuln_scanning_current', 'vuln_remediation_within_timeframe'], rollups: ['vulnerability_findings'] },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['metadata', 'aggregates'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 3000, requestsPerMinute: 120 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Tenable', group: 'Vulnerability scanning' }, vendorSlug: 'tenable',
    form: { fields: [
            secret('access_key', 'Access key', { min: 16, max: 128, tooShort: 'Paste the full access key from Tenable.',
                hint: [link('https://docs.tenable.com/vulnerability-management/Content/Settings/my-account/GenerateAPIKey.htm', 'Generate API keys')] }),
            secret('secret_key', 'Secret key', { min: 16, max: 128, trim: false, tooShort: 'Paste the full secret key from Tenable.' }),
        ] },
});
// ── ML registries (V101-0-4 wires creation + run) ───────────────────────
const mlflow = defineManifest({
    kind: 'mlflow', manifestVersion: 1, lifecycle: 'active', family: 'ml_registry',
    provider: { key: 'mlflow', channel: 'self_hosted' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['mlflow:read'] }],
    // Self-hosted tracking server on the customer's domain (Databricks-hosted MLflow uses *.cloud.databricks.com).
    hosts: { customerSupplied: { field: 'tracking_uri', patterns: ['*.cloud.databricks.com', '*.azuredatabricks.net'], anyPublicHost: true } },
    outputs: { checks: ['artifact_registry'], inventory: ['models'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['metadata'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker', 'customer'],
    limits: { maxRequests: 300, requestsPerMinute: 120 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'MLflow', group: 'ML registry', setup: false }, vendorSlug: 'mlflow',
    form: { fields: [
            { key: 'tracking_uri', label: 'Tracking server URL', store: 'config', validate: 'customer_host', max: 300, host: { path: 'keep', urlMessage: 'Use an https URL like https://mlflow.example.com.' },
                message: 'Use an https URL on a public host name (no IP addresses, localhost or internal names).', placeholder: 'https://mlflow.example.com' },
            secret('token', 'Access token', { required: false, min: 8, max: 4096 }),
        ] },
    blueprint: { slots: ['artifact_registry', 'evaluation_harness'], claims: ['Model registry via MLflow', 'Experiment tracking via MLflow'] },
});
const wandb = defineManifest({
    kind: 'wandb', manifestVersion: 1, lifecycle: 'active', family: 'ml_registry',
    provider: { key: 'wandb', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['wandb:read'] }],
    hosts: { fixed: ['api.wandb.ai'] },
    outputs: { checks: ['artifact_registry'], inventory: ['models'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['metadata'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 300, requestsPerMinute: 120 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Weights & Biases', group: 'ML registry', setup: false, target: { field: 'entity', display: 'raw' } }, vendorSlug: 'wandb',
    form: { fields: [
            { key: 'entity', label: 'Entity', store: 'config', validate: { regex: '^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$' }, message: 'Use the W&B entity (team or user) name.' },
            secret('api_key', 'API key', { min: 20, max: 256 }),
        ] },
    blueprint: { slots: ['artifact_registry', 'telemetry'], claims: ['Model/artifact registry via Weights & Biases', 'Run telemetry via Weights & Biases'] },
});
const purview = defineManifest({
    kind: 'purview', manifestVersion: 1, lifecycle: 'active', family: 'ml_registry',
    provider: { key: 'microsoft-purview', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['purview.catalog.read'] }],
    hosts: { fixed: ['login.microsoftonline.com'], customerSupplied: { field: 'endpoint', patterns: ['*.purview.azure.com'] } },
    outputs: { checks: ['data_lineage'], inventory: ['models'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['metadata'], neverCollects: NEVER },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 300, requestsPerMinute: 120 },
    runner: 'posture', managedBy: 'posture_connectors',
    // The list shows the tenant (the first config key the old target chain found).
    ui: { label: 'Microsoft Purview', group: 'Data governance', setup: false, target: { field: 'tenant_id', display: 'raw' } }, vendorSlug: 'purview',
    form: { fields: [
            { key: 'endpoint', label: 'Purview endpoint', store: 'config', validate: 'customer_host', max: 200, host: { urlMessage: 'Use a URL like https://<account>.purview.azure.com.' },
                message: 'Use https://<account>.purview.azure.com with no path.' },
            { key: 'tenant_id', label: 'Directory (tenant) ID', store: 'config', validate: 'guid' },
            { key: 'client_id', label: 'Application (client) ID', store: 'credential', input: 'text', validate: 'guid' },
            secret('client_secret', 'Client secret', { min: 8, max: 512, trim: false }),
        ] },
    blueprint: { slots: ['data_governance'], claims: ['Data catalog / classification via Microsoft Purview'] },
});
// ── AI providers, gateways, vector stores (V101-3) ─────────────────────
// D-CI-4: config + daily aggregates only; prompt/response bodies never.
const ADMIN_KEY_REVALIDATE_DAYS = 30;
const anthropic = defineManifest({
    kind: 'anthropic', manifestVersion: 1, lifecycle: 'active', family: 'ai_provider',
    provider: { key: 'anthropic', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['anthropic:admin_api_key'] }],
    privilege: {
        beyondReadOnly: ['anthropic:admin_api_key'],
        reason: 'Usage, cost, member and API-key reports are only served to an Admin API key. GAC Pro sends GET requests only.',
        vendorLimitation: 'Anthropic has no read-only Admin API key; every admin key can also manage members, workspaces and keys.',
        revalidateDays: ADMIN_KEY_REVALIDATE_DAYS,
    },
    hosts: { fixed: ['api.anthropic.com'] },
    outputs: { checks: ['ai_provider_admins_minimized', 'ai_provider_keys_rotated', 'ai_provider_audit_logging', 'ai_api_keys_managed'], rollups: ['ai_usage_rollups'], inventory: ['ai_systems'] },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['config', 'aggregates', 'metadata'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 300, requestsPerMinute: 50 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Anthropic', group: 'AI provider' }, vendorSlug: 'anthropic',
    form: { fields: [ADMIN_KEY('sk-ant-admin', '^sk-ant-admin[0-9A-Za-z_-]{10,250}$', 'Anthropic', 'https://console.anthropic.com/settings/admin-keys')] },
});
const CHATGPT_GROUP = { id: 'chatgpt_enterprise', legend: 'ChatGPT Enterprise (optional)', collapsed: true };
const openai = defineManifest({
    kind: 'openai', manifestVersion: 1, lifecycle: 'active', family: 'ai_provider',
    provider: { key: 'openai', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['openai:admin_api_key'] }],
    privilege: {
        beyondReadOnly: ['openai:admin_api_key'],
        reason: 'Projects, keys, audit logs, usage and costs are only served to an organization Admin API key. GAC Pro sends GET requests only.',
        vendorLimitation: 'OpenAI Admin API keys are full-scope; OpenAI offers no read-only admin key.',
        revalidateDays: ADMIN_KEY_REVALIDATE_DAYS,
    },
    // V101-3-3 (D-CI-43): api.chatgpt.com serves the optional ChatGPT Enterprise compliance log LIST (never a file).
    hosts: { fixed: ['api.openai.com', 'api.chatgpt.com'] },
    outputs: { checks: ['ai_provider_admins_minimized', 'ai_provider_keys_rotated', 'ai_provider_audit_logging', 'ai_api_keys_managed',
            'saas_audit_logging_enabled', 'saas_audit_logs_current', 'saas_audit_logs_retained'], rollups: ['ai_usage_rollups'], inventory: ['ai_systems'] },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['config', 'aggregates', 'metadata'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 400, requestsPerMinute: 60 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'OpenAI', group: 'AI provider' }, vendorSlug: 'openai',
    form: { fields: [
            ADMIN_KEY('sk-admin-', '^sk-admin-[0-9A-Za-z_-]{10,250}$', 'OpenAI', 'https://platform.openai.com/settings/organization/admin-keys'),
            // V101-3-3 (D-CI-43/44): optional; the workspace ID is kept in config too so runs know to read it.
            { key: 'chatgpt_workspace_id', label: 'ChatGPT workspace ID', store: 'both', input: 'text', required: false, group: CHATGPT_GROUP,
                validate: { regex: '^[A-Za-z0-9_-]{8,128}$' }, message: 'Use the workspace ID from ChatGPT workspace settings.',
                hint: ['Needed together with the compliance key · In ChatGPT workspace settings · ', link('https://help.openai.com/en/articles/9261474-openai-compliance-platform-for-enterprise-and-edu-customers', 'Find it')] },
            secret('compliance_api_key', 'Compliance API key', { required: false, min: 20, max: 512, tooShort: 'Paste the full compliance key.', group: CHATGPT_GROUP, hintStyle: 'scopes',
                hint: ['Needed together with the workspace ID. Allow only the audit and authentication log categories · ', link('https://help.openai.com/en/articles/9261474-openai-compliance-platform-for-enterprise-and-edu-customers', 'Create a compliance key')] }),
            { key: 'chatgpt_log_index', label: 'Datadog index that keeps these logs', store: 'config', input: 'text', required: false, group: CHATGPT_GROUP,
                validate: { regex: '^[A-Za-z0-9_.-]{1,100}$' }, message: 'Use the index name as shown in Datadog.',
                hint: 'Needs the Datadog connection on this page. The index must keep logs 90 days or more.' },
        ], together: [['chatgpt_workspace_id', 'compliance_api_key']] },
});
const azureOpenai = defineManifest({
    kind: 'azure_openai', manifestVersion: 1, lifecycle: 'active', family: 'ai_provider',
    provider: { key: 'azure', channel: 'azure_openai' },
    // The customer's own app registration (client credentials) holding Reader +
    // Monitoring Reader. The multitenant-consent path (D-CI-15) becomes primary when it ships.
    auth: [
        // ADR-0062 (D-CI-36): admin consent to GAC Pro's multitenant app; no stored credential (federated assertion).
        { kind: 'azure_multitenant', lintProfile: 'generic', scopes: ['azure-role:Reader', 'azure-role:Monitoring Reader'] },
        // The customer's own app registration (client credentials) — the fallback.
        { kind: 'api_token', lintProfile: 'generic', scopes: ['azure-role:Reader', 'azure-role:Monitoring Reader'], weaker: true },
    ],
    hosts: { fixed: ['management.azure.com', 'login.microsoftonline.com'] },
    // V113-2-2 (ADR-0102 §1): Azure Monitor metrics (Monitoring Reader, already granted) as invocation / filter rollups.
    outputs: { checks: ['ai_usage_logging_enabled', 'ai_guardrails_configured', 'ai_prompt_attack_filter', 'ai_model_inventory', 'ai_provider_keys_rotated'], inventory: ['ai_systems'], rollups: ['ai_usage_rollups'] },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['config', 'metadata', 'aggregates'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 600, requestsPerMinute: 300 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Azure OpenAI', group: 'AI provider', target: { field: 'tenant_id', display: 'tail4', noun: 'Tenant' } }, vendorSlug: 'azure',
    form: { fields: [
            { key: 'tenant_id', label: 'Directory (tenant) ID', store: 'both', validate: 'guid', hint: GUID_HINT },
            { key: 'client_id', label: 'Application (client) ID', store: 'credential', input: 'text', validate: 'guid',
                hint: [`${GUID_HINT} · `, link('https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade', 'App registrations')] },
            secret('client_secret', 'Client secret', { min: 8, max: 512, trim: false }),
            { key: 'subscription_ids', label: 'Subscription IDs', store: 'config', input: 'list', validate: 'guid', list: { min: 1, max: 20, tooMany: 'Up to 20 subscriptions.' },
                message: 'Each subscription ID must be a GUID.',
                hint: ['Comma-separated, up to 20. Grant Reader and Monitoring Reader on each subscription · ', link('https://portal.azure.com/#view/Microsoft_Azure_Billing/SubscriptionsBladeV2', 'Find your subscription IDs')] },
        ] },
});
/**
 * V102-9-7: Azure cloud posture. Resource Graph inventory + Defender for
 * Cloud recommendations + Azure Policy compliance. Read-only (Reader +
 * Security Reader). Uses the same multitenant consent + tenant binding
 * as `azure_openai` (V101-3-9 / ADR-0062); the `cloud` form field picks
 * commercial / Azure Government / GCC High endpoints (ADR-0061).
 */
const azurePosture = defineManifest({
    kind: 'azure_posture', manifestVersion: 1, lifecycle: 'active', family: 'cloud',
    provider: { key: 'azure', channel: 'direct' },
    // Only the keyless multitenant consent path (no stored credential). ADR-0053 §1 / ADR-0062.
    auth: [
        { kind: 'azure_multitenant', lintProfile: 'generic', scopes: ['azure-role:Reader', 'azure-role:Security Reader'] },
    ],
    // Both sovereign-cloud ARM + login hosts; the collector picks per `cloud`.
    hosts: { fixed: ['management.azure.com', 'management.usgovcloudapi.net', 'login.microsoftonline.com', 'login.microsoftonline.us'] },
    // V103-4-3 / ADR-0066 A3: Resource Graph listing (Reader, already held).
    outputs: { checks: ['cloud_asset_inventory_recording', 'cloud_benchmarks_passing', 'cloud_config_compliant'], inventory: ['resources'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: NEVER_DATA },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 600, requestsPerMinute: 300 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Azure (posture)', group: 'Cloud account', target: { field: 'tenant_id', display: 'tail4', noun: 'Tenant' } }, vendorSlug: 'azure-posture',
    form: { fields: [
            { key: 'cloud', label: 'Azure cloud', store: 'config', input: 'select', options: AZURE_CLOUD_OPTIONS,
                message: 'Pick the Azure cloud this subscription lives in.' },
            { key: 'tenant_id', label: 'Directory (tenant) ID', store: 'both', validate: 'guid', hint: GUID_HINT },
            { key: 'subscription_ids', label: 'Subscription IDs', store: 'config', input: 'list', validate: 'guid',
                list: { min: 1, max: 20, tooMany: 'Up to 20 subscriptions.' },
                message: 'Each subscription ID must be a GUID.',
                hint: ['Comma-separated, up to 20. Grant Reader and Security Reader on each subscription · ',
                    link('https://portal.azure.com/#view/Microsoft_Azure_Billing/SubscriptionsBladeV2', 'Find your subscription IDs')] },
        ] },
});
const cloudflareAiGateway = defineManifest({
    kind: 'cloudflare_ai_gateway', manifestVersion: 2, lifecycle: 'active', family: 'ai_gateway',
    provider: { key: 'cloudflare', channel: 'gateway', channels: ['gateway', 'direct', 'bedrock', 'azure_openai'] },
    // V113-2-2 / ADR-0102 §6 (manifest v2): GraphQL analytics (aggregate dataset aiGatewayRequestsAdaptiveGroups, never logs).
    auth: [{ kind: 'api_token', lintProfile: 'cloudflare_read', scopes: ['AI Gateway Read', 'Account Analytics Read'] }],
    scopeChanges: [{ version: 2, added: ['Account Analytics Read'], reason: 'V113-2-2: AI Gateway request, token, cache, error and cost analytics as daily rollups (ADR-0102)' }],
    hosts: { fixed: ['api.cloudflare.com'] },
    outputs: {
        checks: ['ai_usage_logging_enabled', 'ai_log_retention_set', 'ai_rate_limiting'], rollups: ['ai_usage_rollups'], inventory: ['ai_systems'],
        // V105-4-3: every check reads the AI Gateway product only.
        checkProducts: { ai_usage_logging_enabled: ['ai-gateway'], ai_log_retention_set: ['ai-gateway'], ai_rate_limiting: ['ai-gateway'] },
    },
    captureShapes: ['posture', 'bulk'],
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 500, requestsPerMinute: 240 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Cloudflare AI Gateway', group: 'AI gateway', target: { field: 'account_id', display: 'tail4', noun: 'Account' } }, vendorSlug: 'cloudflare',
    form: { fields: [
            { key: 'account_id', label: 'Account ID', store: 'config', validate: { regex: '^[0-9a-f]{32}$' }, message: 'The account ID is 32 lowercase hex characters.',
                hint: '32 characters, on the Cloudflare dashboard overview.' },
            secret('api_token', 'API token', { min: 20, max: 256, tooShort: 'This token is too short.',
                // V113-2-2 (manifest v2): both read permissions the token needs, as the Cloudflare token form names them.
                hint: ['Read-only permissions ', { code: 'AI Gateway Read' }, ' and ', { code: 'Account Analytics Read' }, ' · ', link('https://dash.cloudflare.com/profile/api-tokens', 'Create a token')] }),
        ] },
    blueprint: { slots: ['runtime_enforcement'], claims: ['AI traffic controls (logging, rate limits) via Cloudflare AI Gateway'] },
});
/**
 * V103-8-3 (V97-2-9; kind added by migration 220, activated by 345): Cloudflare
 * account posture. A customer-created API token holding only the read
 * permission groups below (cloudflare_read lint: "<Group> Read", never
 * Edit/Write). Checks are program-neutral evidence (D-103-14): they reach
 * program objectives through test check-aliases, not new SEC-* edges.
 */
const HOSTNAME_RE = '^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\\.)+[a-z]{2,63}$';
const cloudflareAccount = defineManifest({
    kind: 'cloudflare', manifestVersion: 1, lifecycle: 'active', family: 'cloud',
    provider: { key: 'cloudflare', channel: 'direct' },
    // V109-3-3 (PRD §7, owner 2026-10-13): `D1 Read` is the slice's one added scope — read-only, for the
    // D1 gap rule's jurisdiction (docs/v10.9/slice.md R19). Database contents are never read.
    auth: [{ kind: 'api_token', lintProfile: 'cloudflare_read', scopes: [
                'Account Settings Read', 'Account API Tokens Read', 'Zone Read', 'Zone Settings Read', 'DNS Read', 'Zone WAF Read',
                'Access: Apps and Policies Read', 'Workers Scripts Read', 'Workers Routes Read', 'D1 Read',
            ] }],
    hosts: { fixed: ['api.cloudflare.com'] },
    outputs: {
        checks: ['idp_mfa_enforced', 'cloud_service_credentials_rotated', 'edge_dnssec_enabled', 'edge_tls_enforced', 'edge_hsts_enabled',
            'edge_waf_managed_rules', 'saas_audit_logging_enabled', 'edge_admin_access_protected', 'cloud_asset_inventory_recording',
            // V109-3-3 (slice R19): decided by the D1 gap rule from observations, not by the collector.
            'd1_data_location_restricted'],
        hints: CLOUDFLARE_HINTS,
        // V105-4-3: the inventory check counts Workers scripts; the zone / account checks read no product.
        checkProducts: { cloud_asset_inventory_recording: ['workers'], d1_data_location_restricted: ['d1'] },
    },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'aggregates', 'identities_hashed'], neverCollects: NEVER_DATA },
    schedule: DAILY, runnableIn: ['worker'],
    // 9 account-level reads + 6 per zone (at most 50 zones evaluated per run) + the D1 list and one read per database (at most 50).
    limits: { maxRequests: 460, requestsPerMinute: 240 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Cloudflare', group: 'Cloud account', target: { field: 'account_id', display: 'tail4', noun: 'Account' } }, vendorSlug: 'cloudflare',
    form: { fields: [
            { key: 'account_id', label: 'Account ID', store: 'config', validate: { regex: '^[0-9a-f]{32}$' }, message: 'The account ID is 32 lowercase hex characters.',
                hint: '32 characters, on the Cloudflare dashboard overview.' },
            secret('api_token', 'API token', { min: 20, max: 256, tooShort: 'This token is too short.', hintStyle: 'scopes',
                hint: [link('https://dash.cloudflare.com/profile/api-tokens', 'Create a token'), ' · Grant only: ', { code: 'Account Settings Read' }, { code: 'Account API Tokens Read' },
                    { code: 'Zone Read' }, { code: 'Zone Settings Read' }, { code: 'DNS Read' }, { code: 'Zone WAF Read' }, { code: 'Access: Apps and Policies Read' },
                    { code: 'Workers Scripts Read' }, { code: 'Workers Routes Read' }, { code: 'D1 Read' }] }),
            { key: 'production_zones', label: 'Production zones', store: 'config', input: 'list', required: false, validate: { regex: HOSTNAME_RE },
                list: { max: 50, tooMany: 'Up to 50 zones.' }, message: 'Use zone names like example.com.',
                hint: 'Comma-separated. The WAF check covers these zones; leave blank to check every active zone.' },
            { key: 'admin_hostnames', label: 'Admin hostnames', store: 'config', input: 'list', required: false, validate: { regex: HOSTNAME_RE },
                list: { max: 20, tooMany: 'Up to 20 hostnames.' }, message: 'Use host names like admin.example.com.',
                hint: 'Comma-separated. Each must sit behind a Cloudflare Access application.' },
        ] },
});
const PINECONE_SA_GROUP = { id: 'service_account', legend: 'Service account (optional)' };
const pinecone = defineManifest({
    kind: 'pinecone', manifestVersion: 1, lifecycle: 'active', family: 'vector_db',
    provider: { key: 'pinecone', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['pinecone:indexes.read', 'pinecone:admin_service_account'] }],
    // D-CI-34: the optional service account reads the Admin API (projects, API keys and
    // their roles, service accounts). Pinecone documents no read-only role that lists
    // API keys [U: verify with a test account], so the credential is declared.
    privilege: {
        beyondReadOnly: ['pinecone:admin_service_account'],
        reason: 'Projects, API keys and their roles, and service accounts are only served to an Admin API service account. GAC Pro sends GET requests only.',
        vendorLimitation: 'Pinecone has no documented read-only role for listing API keys; the service account role may also create or delete keys.',
        revalidateDays: ADMIN_KEY_REVALIDATE_DAYS,
        onlyWithFields: ['admin_client_id', 'admin_client_secret'],
    },
    hosts: { fixed: ['api.pinecone.io', 'login.pinecone.io'] },
    outputs: { checks: ['vector_store_encrypted', 'ai_api_keys_managed'], inventory: ['ai_systems'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER_BODIES_DATA },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 120, requestsPerMinute: 60 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Pinecone', group: 'Vector database' }, vendorSlug: 'pinecone',
    form: { fields: [
            secret('api_key', 'API key', { min: 20, max: 256, tooShort: 'This key is too short.', hint: [link('https://app.pinecone.io/', 'Find your API keys')] }),
            // The OAuth client ID is not a secret; kept in config too so runs can tell the admin path is in use.
            { key: 'admin_client_id', label: 'Client ID', store: 'both', input: 'text', required: false, group: PINECONE_SA_GROUP,
                validate: { regex: '^[A-Za-z0-9_-]{16,128}$' }, message: 'Use the client ID shown when you created the service account.',
                hint: ['Lets GAC Pro check your API keys and who can use them · ', link('https://docs.pinecone.io/guides/organizations/manage-service-accounts', 'Create a service account')] },
            secret('admin_client_secret', 'Client secret', { required: false, min: 16, max: 512, tooShort: 'Paste the full client secret.', group: PINECONE_SA_GROUP }),
        ], together: [['admin_client_id', 'admin_client_secret']] },
});
const datadog = defineManifest({
    kind: 'datadog', manifestVersion: 1, lifecycle: 'active', family: 'observability',
    provider: { key: 'datadog', channel: 'direct' },
    // API key + a SCOPED application key holding exactly these scopes; an unscoped or over-scoped key is refused (D-CI-40).
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['logs_read_config', 'audit_logs_read', 'security_monitoring_rules_read', 'security_monitoring_notification_profiles_read'] }],
    hosts: { fixed: ['api.datadoghq.com', 'api.us3.datadoghq.com', 'api.us5.datadoghq.com', 'api.ap1.datadoghq.com', 'api.ap2.datadoghq.com', 'api.datadoghq.eu', 'api.ddog-gov.com'] },
    // V101-3-7 (D-CI-41): Cloud SIEM detections on and alerting (SEC-LOG-03), counts only.
    // V102-9-6: SIEM-content checks (coverage, retention, logging-failure alerts) — counts only, no log bodies.
    outputs: { checks: ['ai_provider_audit_logging', 'ai_log_retention_set', 'siem_security_alerting', 'siem_coverage_identity_logs', 'siem_retention_ge_365d', 'siem_alerts_logging_failure'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 20, requestsPerMinute: 20 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Datadog', group: 'Observability', target: { field: 'site', display: 'option' } }, vendorSlug: 'datadog',
    form: { fields: [
            { key: 'site', label: 'Datadog site', store: 'config', input: 'select', options: DATADOG_SITE_OPTIONS,
                message: 'Choose a Datadog site.' },
            secret('api_key', 'API key', { validate: { regex: '^[0-9a-f]{32}$' }, message: 'The API key is 32 hex characters.',
                hint: [link('https://{site.app_host}/organization-settings/api-keys', 'Find your API keys')] }),
            secret('app_key', 'Application key', { validate: { regex: '^[0-9a-f]{40}$' }, message: 'The application key is 40 hex characters.', hintStyle: 'scopes',
                hint: [link('https://{site.app_host}/organization-settings/application-keys', 'Create an application key'), ' · When creating it, grant only: ', { code: 'logs_read_config' }, { code: 'audit_logs_read' }, { code: 'security_monitoring_rules_read' }, { code: 'security_monitoring_notification_profiles_read' }] }),
        ] },
    blueprint: { slots: ['telemetry'], claims: ['Log retention and audit trail in Datadog'] },
});
// ── SIEM (V102-9-6): read-only SIEM-content coverage/retention/alerts. Count-only queries; log bodies are never read. ──
const SIEM_CHECKS = ['siem_coverage_identity_logs', 'siem_retention_ge_365d', 'siem_alerts_logging_failure'];
const splunk = defineManifest({
    // Held `planned` until migration 305 lands via the batch PR (D-CI-54, 10.1 2026-10-06).
    // Flipping to `active` in the SPA before the connector_kinds row exists would FK-fail on connect.
    kind: 'splunk', manifestVersion: 1, lifecycle: 'active', family: 'siem',
    provider: { key: 'splunk', channel: 'direct' },
    // Search-head read-only token with the "search" capability.
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['splunk:search_read'] }],
    // Splunk Cloud hosts; on-prem Splunk is not addressable from a Worker (would need the customer collector).
    hosts: { customerSupplied: { field: 'host', patterns: ['*.splunkcloud.com'] } },
    outputs: { checks: [...SIEM_CHECKS] },
    captureShapes: ['posture'],
    // V102-9-6: tstats counts and index metadata only; no log bodies ever.
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: ['secrets', 'raw_pii'] },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 30, requestsPerMinute: 30 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Splunk', group: 'Observability', target: { field: 'host', display: 'host' } }, vendorSlug: 'splunk',
    form: { fields: [
            { key: 'host', label: 'Splunk Cloud host', store: 'config', validate: 'customer_host', max: 200,
                message: 'Use the Splunk Cloud host like https://<stack>.splunkcloud.com with no path.',
                host: { urlMessage: 'Use an https URL like https://<stack>.splunkcloud.com.' },
                placeholder: 'https://example.splunkcloud.com' },
            secret('token', 'Search-head read token', { min: 20, max: 2048, tooShort: 'Paste the full Splunk token.',
                hint: 'Create a read-only token with the "search" capability.' }),
        ] },
    blueprint: { slots: ['telemetry'], claims: ['SIEM content coverage and retention in Splunk'] },
});
// Microsoft Sentinel on Azure Log Analytics. ADR-0061 cloud variants: commercial (loganalytics.io),
// usgov / gcc_high (loganalytics.us). Primary auth is the ADR-0062 multitenant consent; the customer's
// own app registration (client credentials) is the weaker fallback used by the current form create path.
const sentinel = defineManifest({
    kind: 'sentinel', manifestVersion: 1, lifecycle: 'active', family: 'siem',
    provider: { key: 'microsoft-sentinel', channel: 'direct' },
    auth: [
        { kind: 'azure_multitenant', lintProfile: 'generic', scopes: ['azure-role:Log Analytics Reader', 'azure-role:Monitoring Reader'] },
        { kind: 'api_token', lintProfile: 'generic', scopes: ['azure-role:Log Analytics Reader', 'azure-role:Monitoring Reader'], weaker: true },
    ],
    // Fixed hosts for commercial + US Gov Log Analytics plus ARM + login.
    hosts: { fixed: ['api.loganalytics.io', 'api.loganalytics.us', 'management.azure.com', 'management.usgovcloudapi.net', 'login.microsoftonline.com', 'login.microsoftonline.us'] },
    outputs: { checks: [...SIEM_CHECKS] },
    captureShapes: ['posture'],
    // KQL `count` and metadata only; row contents of any table are never returned.
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: ['secrets', 'raw_pii'] },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 60, requestsPerMinute: 60 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Microsoft Sentinel', group: 'Observability', target: { field: 'workspace_id', display: 'tail4', noun: 'Workspace' } }, vendorSlug: 'sentinel',
    form: { fields: [
            { key: 'tenant_id', label: 'Directory (tenant) ID', store: 'both', validate: 'guid', hint: GUID_HINT },
            { key: 'workspace_id', label: 'Log Analytics workspace ID', store: 'config', validate: 'guid', hint: GUID_HINT },
            { key: 'subscription_id', label: 'Subscription ID', store: 'config', validate: 'guid', hint: GUID_HINT },
            { key: 'resource_group', label: 'Resource group', store: 'config', validate: { regex: '^[A-Za-z0-9._()-]{1,90}$' }, message: 'Use the workspace resource group name.' },
            { key: 'workspace_name', label: 'Workspace name', store: 'config', validate: { regex: '^[A-Za-z0-9][A-Za-z0-9-]{3,62}$' }, message: 'Use the Log Analytics workspace name.' },
            { key: 'cloud', label: 'Azure cloud', store: 'config', input: 'select',
                options: [
                    { value: 'commercial', label: 'Commercial · loganalytics.io' },
                    { value: 'usgov', label: 'US Government · loganalytics.us' },
                    { value: 'gcc_high', label: 'GCC High · loganalytics.us' },
                ],
                message: 'Choose an Azure cloud.' },
            { key: 'client_id', label: 'Application (client) ID', store: 'credential', input: 'text', required: false, validate: 'guid',
                hint: 'Required for the direct (customer app registration) path; leave blank when using Microsoft admin consent.' },
            secret('client_secret', 'Client secret', { required: false, min: 8, max: 512, trim: false }),
        ], together: [['client_id', 'client_secret']] },
    blueprint: { slots: ['telemetry'], claims: ['SIEM content coverage and retention in Microsoft Sentinel'] },
});
// ── Data platforms (V101-7-3: Tier 2 G-1 proof) ────────────────────────
// PLANNED (owner decision 2026-10-05): not creatable until v11.x / the
// customer-run collector — Snowflake network policies block unfixed Workers
// egress and the collector is untested on a real account. The collector
// (adapters/snowflake-collector.ts) and its tests stay as the G-1 reference;
// docs/v10.1/g1-tier2-readiness.md lists the wiring activation needs.
// Four aggregate SELECTs on SNOWFLAKE.ACCOUNT_USAGE via the SQL API v2. The
// customer grants a dedicated role SNOWFLAKE.SECURITY_VIEWER + USAGE on one
// warehouse and issues a PAT restricted to that role. No customer tables.
const snowflake = defineManifest({
    kind: 'snowflake', manifestVersion: 1, lifecycle: 'planned', family: 'saas',
    provider: { key: 'snowflake', channel: 'direct' },
    auth: [{ kind: 'api_token', lintProfile: 'generic', scopes: ['snowflake-database-role:SNOWFLAKE.SECURITY_VIEWER', 'snowflake-warehouse:USAGE'] }],
    hosts: { customerSupplied: { field: 'account_url', patterns: ['*.snowflakecomputing.com'] } },
    outputs: { checks: ['idp_mfa_enforced', 'idp_admins_minimized', 'cloud_network_restricted', 'saas_audit_logging_enabled'], hints: SNOWFLAKE_HINTS },
    captureShapes: ['posture'],
    // Cortex usage history exists in the same schema; bodies are never read. No customer table is ever selected.
    dataBoundary: { collects: ['config', 'aggregates'], neverCollects: NEVER_BODIES_DATA },
    // On activation: schedule DAILY, runnableIn ['worker'], runner 'posture',
    // limits { maxRequests: 20, requestsPerMinute: 20 } (4 statements + ≤3 polls each).
    runnableIn: [], runner: 'none', managedBy: 'posture_connectors',
    ui: { label: 'Snowflake', group: 'Data platform' }, vendorSlug: 'snowflake',
    form: { fields: [
            { key: 'account_url', label: 'Account URL', store: 'config', validate: 'customer_host', max: 200, placeholder: 'https://yourorg-youraccount.snowflakecomputing.com',
                message: 'Use https://<account>.snowflakecomputing.com with no path.',
                host: { deny: [{ pattern: '*.privatelink.snowflakecomputing.com', message: 'PrivateLink account URLs are not reachable from GAC Pro; use the public account URL.' }] } },
            { key: 'role', label: 'Role', store: 'config', validate: 'identifier', message: 'Use the unquoted role name.', placeholder: 'GACPRO_READONLY', hintStyle: 'scopes',
                hint: ['Grant it ', { code: 'SNOWFLAKE.SECURITY_VIEWER' }, ' and ', { code: 'USAGE' }, ' on the warehouse'] },
            { key: 'warehouse', label: 'Warehouse', store: 'config', validate: 'identifier', message: 'Use the unquoted warehouse name.', placeholder: 'GACPRO_WH' },
            secret('token', 'Programmatic access token', { min: 20, max: 4096, tooShort: 'This token is too short.',
                hint: ['Restricted to the role above · ', link('https://docs.snowflake.com/en/user-guide/programmatic-access-tokens', 'Generate a token')] }),
        ] },
    // G-1k: a data platform is a database in the asset register, not "other".
    asset: { tier: 'database' },
    defaultRiskTier: 'high',
});
// ── Self-hosted AI + Kubernetes (V101-8-2/8-3, ADR-0055) ────────────────
// Qdrant, Weaviate, Ollama and vLLM are reached only by the customer-run
// collector (no Worker can reach a private address, and none is ever pointed
// at one): runnableIn ['customer'], hosts.privateHosts, and the rows are created
// by the collector ingest on its first accepted push (managedBy
// 'customer_collector', auth collector_key), never by a create route. Target
// credentials stay in the customer's Secrets and are never sent here.
// D-CI-4: configuration only. Qdrant points/payloads, Weaviate objects, and
// every prompt and completion are never read. Inventory-only: no catalogued
// check maps these facts to a control yet (a service-authentication check key
// would need a new catalog entry, which is a content decision).
const COLLECTOR_LIMITS = { maxRequests: 200, requestsPerMinute: 120 };
const COLLECTOR_HOSTS = { customerSupplied: { field: 'base_url', patterns: [], privateHosts: true } };
const qdrant = defineManifest({
    kind: 'qdrant', manifestVersion: 1, lifecycle: 'active', family: 'vector_db',
    provider: { key: 'qdrant', channel: 'self_hosted' },
    // A Qdrant read-only API key, held in the customer's Secret.
    auth: [{ kind: 'collector_key', lintProfile: 'generic', scopes: ['qdrant:read_only_api_key'] }],
    hosts: COLLECTOR_HOSTS,
    outputs: { checks: ['ai_service_authn_required'], inventory: ['ai_systems'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER_BODIES_DATA },
    schedule: DAILY, runnableIn: ['customer'], limits: COLLECTOR_LIMITS,
    runner: 'none', managedBy: 'customer_collector',
    ui: { label: 'Qdrant (customer-run)', group: 'Vector database' }, vendorSlug: 'qdrant',
});
const weaviate = defineManifest({
    kind: 'weaviate', manifestVersion: 1, lifecycle: 'active', family: 'vector_db',
    provider: { key: 'weaviate', channel: 'self_hosted' },
    auth: [{ kind: 'collector_key', lintProfile: 'generic', scopes: ['weaviate:schema.read'] }],
    hosts: COLLECTOR_HOSTS,
    outputs: { checks: ['ai_service_authn_required'], inventory: ['ai_systems'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER_BODIES_DATA },
    schedule: DAILY, runnableIn: ['customer'], limits: COLLECTOR_LIMITS,
    runner: 'none', managedBy: 'customer_collector',
    ui: { label: 'Weaviate (customer-run)', group: 'Vector database' }, vendorSlug: 'weaviate',
});
const ollama = defineManifest({
    kind: 'ollama', manifestVersion: 1, lifecycle: 'active', family: 'inference_runtime',
    provider: { key: 'ollama', channel: 'self_hosted' },
    // Ollama has no auth of its own; reaching /api/tags needs none. A `:cloud` model resolves to
    // channel ollama_cloud (an external boundary), not self_hosted (V101-8-3).
    auth: [{ kind: 'collector_key', lintProfile: 'generic', scopes: ['ollama:tags.read'] }],
    hosts: COLLECTOR_HOSTS,
    outputs: { checks: ['ai_service_authn_required'], inventory: ['models'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['customer'], limits: COLLECTOR_LIMITS,
    runner: 'none', managedBy: 'customer_collector',
    ui: { label: 'Ollama (customer-run)', group: 'Inference runtime' }, vendorSlug: 'ollama',
});
const vllm = defineManifest({
    kind: 'vllm', manifestVersion: 1, lifecycle: 'active', family: 'inference_runtime',
    provider: { key: 'vllm', channel: 'self_hosted' },
    auth: [{ kind: 'collector_key', lintProfile: 'generic', scopes: ['vllm:models.read'] }],
    hosts: COLLECTOR_HOSTS,
    outputs: { checks: ['ai_service_authn_required'], inventory: ['models'] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['customer'], limits: COLLECTOR_LIMITS,
    runner: 'none', managedBy: 'customer_collector',
    ui: { label: 'vLLM (customer-run)', group: 'Inference runtime' }, vendorSlug: 'vllm',
});
// Cluster listing from a Worker (EKS/GKE/AKS cloud APIs) rides the existing aws/gcp/azure
// credentials; the Kubernetes API of a private cluster is customer-collector only. Hosts
// are fixed cloud endpoints only: the in-cluster API server is addressed by the collector's
// service account, never by a manifest host. runner 'none' until the Worker listing is wired
// into the aws/gcp runs (adapters/kubernetes-cloud-collector.ts).
const AWS_EKS_SCOPES = ['aws-policy:SecurityAudit']; // SecurityAudit carries eks:List*/Describe*
const kubernetes = defineManifest({
    kind: 'kubernetes', manifestVersion: 1, lifecycle: 'active', family: 'kubernetes',
    provider: { key: 'kubernetes', channel: 'self_hosted' },
    auth: [
        { kind: 'aws_oidc', lintProfile: 'iam_policy', scopes: AWS_EKS_SCOPES },
        { kind: 'aws_role', lintProfile: 'iam_policy', scopes: AWS_EKS_SCOPES, weaker: true },
        { kind: 'gcp_wif', lintProfile: 'gcp_roles', scopes: ['roles/container.viewer'] },
        { kind: 'gcp_sa_key', lintProfile: 'gcp_roles', scopes: ['roles/container.viewer'], weaker: true },
        { kind: 'api_token', lintProfile: 'generic', scopes: ['azure-role:Reader'] },
        // The Kubernetes API from the customer collector: get/list/watch on five resources, nothing else.
        { kind: 'collector_key', lintProfile: 'kubernetes_rbac', scopes: K8S_RBAC_SCOPES },
    ],
    hosts: { fixed: ['eks.{region}.amazonaws.com', ...AWS_STS_HOSTS, 'container.googleapis.com', 'management.azure.com', 'login.microsoftonline.com', 'sts.googleapis.com', 'iamcredentials.googleapis.com', 'oauth2.googleapis.com'] },
    // V103-4-3 / ADR-0066 A3: workloads as boundary-component suggestions (same get/list RBAC).
    outputs: { checks: [], inventory: ['ai_systems', 'resources'] },
    captureShapes: ['posture'],
    // Images, namespaces, replica counts, services only: never secrets, env values, configmaps or exec.
    dataBoundary: { collects: ['config', 'metadata'], neverCollects: NEVER_BODIES },
    schedule: DAILY, runnableIn: ['worker', 'customer'], limits: { maxRequests: 300, requestsPerMinute: 120 },
    runner: 'none', managedBy: 'customer_collector',
    ui: { label: 'Kubernetes', group: 'Container platform' }, vendorSlug: 'kubernetes',
});
// ── Push / upload (no collector) ────────────────────────────────────────
function ingest(kind, label, checks, inventory) {
    return {
        kind, manifestVersion: 1, lifecycle: 'active', family: 'ingest',
        provider: { key: 'gacpro', channel: 'direct' },
        // Stored auth_kind is 'none' for all three (the CI token authenticates the push, not the connector row).
        auth: [{ kind: 'none', lintProfile: 'none', scopes: [] }],
        hosts: {}, outputs: { checks, inventory }, captureShapes: ['posture'],
        dataBoundary: { collects: ['metadata'], neverCollects: NEVER },
        runnableIn: [], runner: 'none',
        // vuln_scanner rows are created on the connections page; sbom / local_upload by the system.
        managedBy: kind === 'vuln_scanner' ? 'posture_connectors' : 'system',
        ui: { label, group: kind === 'vuln_scanner' ? 'Vulnerability scanning' : 'Uploads and CI' },
    };
}
// ADR-0074 §6 (migration 468): one connector row per verification-runner identity, created by
// the result ingest on the first accepted standing result (V105-7-5). Results are pushed with a
// CI token (api_token) or the ADR-0055 collector key; GAC Pro calls nothing (ADR-0017).
const verificationRunner = {
    ...ingest('verification_runner', 'Verification runner', []),
    auth: [
        { kind: 'api_token', lintProfile: 'none', scopes: [] },
        { kind: 'collector_key', lintProfile: 'none', scopes: [] },
    ],
};
// ── ITSM (V102-9-5 collectors, V104-F-3 activation): change / incident / access-request tickets as populations
// for the reconciliation + sampling engines. Ticket summary, description and comment bodies are never read.
const jira = defineManifest({
    kind: 'jira', manifestVersion: 1, lifecycle: 'active', family: 'saas',
    provider: { key: 'jira', channel: 'direct' },
    // Jira Cloud REST v3 over HTTP Basic: the account email plus an Atlassian API token (D-104-8).
    auth: [{ kind: 'basic_api_key', lintProfile: 'generic', scopes: ['read:jira-work', 'read:jira-user'] }],
    hosts: { customerSupplied: { field: 'base_url', patterns: ['*.atlassian.net'] } },
    outputs: { checks: ['change_tickets_have_approver'], populations: ['jira_changes', 'jira_incidents', 'jira_access_requests'] },
    captureShapes: ['posture', 'population'],
    dataBoundary: { collects: ['config', 'identities_hashed'], neverCollects: NEVER_DATA },
    schedule: DAILY, runnableIn: ['worker'],
    // Three populations, up to 10 pages each.
    limits: { maxRequests: 40, requestsPerMinute: 30 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Jira', group: 'Ticketing', target: { field: 'base_url', display: 'host' } }, vendorSlug: 'jira',
    form: { fields: [
            { key: 'base_url', label: 'Jira Cloud site', store: 'config', validate: 'customer_host', max: 200,
                message: 'Use your Jira Cloud site like https://<org>.atlassian.net with no path.',
                host: { urlMessage: 'Use an https URL like https://<org>.atlassian.net.' },
                placeholder: 'https://example.atlassian.net' },
            { key: 'email', label: 'Account email', store: 'config', max: 254, validate: { regex: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$' },
                message: 'Enter the email address of the Atlassian account that owns the API token.',
                hint: 'Use a dedicated account with read access only.', placeholder: 'svc-gacpro@example.com' },
            secret('token', 'API token', { min: 20, max: 2048, tooShort: 'Paste the full API token.',
                hint: [link('https://id.atlassian.com/manage-profile/security/api-tokens', 'Create an Atlassian API token'), ' · Create it without scopes. The account needs Browse Projects access only. Tokens expire, so set a reminder to replace it.'] }),
            { key: 'approver_field', label: 'Approver field', store: 'config', required: false, max: 60, validate: { regex: '^customfield_[A-Za-z0-9_]{1,40}$' },
                message: 'Use the field id, like customfield_10042.',
                hint: 'The field on Change issues that records the approver. Leave blank to read tickets without the approval check.', placeholder: 'customfield_10042' },
            { key: 'change_issue_type', label: 'Change issue type', store: 'config', required: false, max: 60, validate: { regex: '^[A-Za-z0-9 _-]{1,60}$' },
                message: 'Use the issue type name, with letters, digits, spaces, hyphens or underscores.',
                hint: 'Leave blank to use Change.', placeholder: 'Change' },
        ] },
});
const servicenow = defineManifest({
    kind: 'servicenow', manifestVersion: 1, lifecycle: 'active', family: 'saas',
    provider: { key: 'servicenow', channel: 'direct' },
    // Table API over HTTP Basic with a dedicated read-only integration user (D-104-8; the collector does not run an OAuth flow).
    auth: [{ kind: 'basic_api_key', lintProfile: 'generic', scopes: ['servicenow:table_api_read'] }],
    hosts: { customerSupplied: { field: 'base_url', patterns: ['*.service-now.com', '*.servicenowservices.com'] } },
    outputs: { checks: ['change_tickets_have_approver'], populations: ['servicenow_changes', 'servicenow_incidents', 'servicenow_access_requests'] },
    captureShapes: ['posture', 'population'],
    dataBoundary: { collects: ['config', 'identities_hashed'], neverCollects: NEVER_DATA },
    schedule: DAILY, runnableIn: ['worker'],
    limits: { maxRequests: 40, requestsPerMinute: 30 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'ServiceNow', group: 'Ticketing', target: { field: 'base_url', display: 'host' } }, vendorSlug: 'servicenow',
    form: { fields: [
            { key: 'base_url', label: 'ServiceNow instance', store: 'config', validate: 'customer_host', max: 200,
                message: 'Use your instance like https://<instance>.service-now.com with no path.',
                host: { urlMessage: 'Use an https URL like https://<instance>.service-now.com.' },
                placeholder: 'https://example.service-now.com' },
            { key: 'user', label: 'Integration user', store: 'config', max: 100, validate: { regex: '^[^\\s:]+$' },
                message: 'Use the user name of a dedicated integration user (no spaces or colons).',
                hint: 'Create a user with web service access only and read access to change, incident and request records.', placeholder: 'svc-gacpro' },
            secret('password', 'Password', { min: 8, max: 2048, tooShort: 'Paste the full password.', hint: 'The password of the integration user above.' }),
        ] },
});
// ── Ticketing write path (V111-0-6 / ADR-0096, D-111-15) ────────────────
// The ONE connector class that writes into a customer system: it creates
// remediation tickets for findings and transitions the tickets IT created
// when the finding resolves (ADR-0017's "detect + recommend + ticket").
// Separate kind from the read-only `jira` evidence collector so evidence
// integrity never depends on a write-capable credential. The write scope
// is a D-CI-12 declaration, shown to the customer at connect time.
const jiraTicketing = defineManifest({
    kind: 'jira_ticketing', manifestVersion: 1, lifecycle: 'active', family: 'saas',
    provider: { key: 'jira', channel: 'direct' },
    auth: [{ kind: 'basic_api_key', lintProfile: 'generic', scopes: ['read:jira-work', 'write:jira-work'] }],
    privilege: {
        beyondReadOnly: ['write:jira-work'],
        reason: 'Creates one remediation ticket per open finding and moves that ticket to Done when the finding closes. It never edits, deletes or transitions tickets it did not create, and never reads ticket bodies.',
        vendorLimitation: 'Jira has no create-only grant: any credential that can create issues can also edit them. Use a dedicated service account restricted to the remediation project.',
        revalidateDays: 90,
    },
    hosts: { customerSupplied: { field: 'base_url', patterns: ['*.atlassian.net'] } },
    outputs: { checks: [] },
    captureShapes: ['posture'],
    dataBoundary: { collects: ['config'], neverCollects: NEVER_DATA },
    schedule: DAILY, runnableIn: ['worker'],
    // Up to 25 creates + transition lookups per run.
    limits: { maxRequests: 80, requestsPerMinute: 30 },
    runner: 'posture', managedBy: 'posture_connectors',
    ui: { label: 'Jira ticketing', group: 'Ticketing', target: { field: 'base_url', display: 'host' } }, vendorSlug: 'jira',
    form: { fields: [
            { key: 'base_url', label: 'Jira Cloud site', store: 'config', validate: 'customer_host', max: 200,
                message: 'Use your Jira Cloud site like https://<org>.atlassian.net with no path.',
                host: { urlMessage: 'Use an https URL like https://<org>.atlassian.net.' },
                placeholder: 'https://example.atlassian.net' },
            { key: 'email', label: 'Account email', store: 'config', max: 254, validate: { regex: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$' },
                message: 'Enter the email address of the Atlassian account that owns the API token.',
                hint: 'Use a dedicated service account whose project access is limited to the remediation project below.', placeholder: 'svc-gacpro@example.com' },
            secret('token', 'API token', { min: 20, max: 2048, tooShort: 'Paste the full API token.',
                hint: [link('https://id.atlassian.com/manage-profile/security/api-tokens', 'Create an Atlassian API token'), ' · This token CREATES tickets, so scope the account to one project. Tokens expire; set a reminder to replace it.'] }),
            { key: 'project_key', label: 'Project key', store: 'config', max: 20, validate: { regex: '^[A-Z][A-Z0-9_]{1,19}$' },
                message: 'Use the Jira project key, like SEC.',
                hint: 'Remediation tickets are created in this project only.', placeholder: 'SEC' },
            { key: 'issue_type', label: 'Issue type', store: 'config', required: false, max: 60, validate: { regex: '^[A-Za-z0-9 _-]{1,60}$' },
                message: 'Use the issue type name, with letters, digits, spaces, hyphens or underscores.',
                hint: 'Leave blank to use Task.', placeholder: 'Task' },
        ] },
});
// ── Planned: declared in the enum, no collector yet (not creatable) ─────
function planned(kind, family, providerKey, label, group, defaultRiskTier) {
    const never = family === 'observability' ? NEVER_BODIES : ['saas', 'cloud', 'vector_db'].includes(family) ? NEVER_DATA : NEVER;
    return {
        kind, manifestVersion: 1, lifecycle: 'planned', family,
        provider: { key: providerKey, channel: 'direct' },
        auth: [{ kind: 'api_token', lintProfile: 'none', scopes: [] }],
        hosts: {}, outputs: { checks: [] }, captureShapes: ['posture'],
        dataBoundary: { collects: ['config'], neverCollects: never },
        runnableIn: [], runner: 'none', managedBy: 'connectors', ui: { label, group }, vendorSlug: providerKey,
        ...(defaultRiskTier ? { defaultRiskTier } : {}),
    };
}
/** The reviewed registry (code). Tests may swap it via `@aigovops/core/testing` only. */
const REGISTERED = [
    oktaPosture, entraPosture, googleWorkspace, microsoft365, oktaGrants, entraGrants,
    aws, gcp,
    github,
    tenable,
    mlflow, wandb, purview,
    cloudflareAccount,
    anthropic, openai, azureOpenai, azurePosture, cloudflareAiGateway, pinecone, datadog,
    splunk, sentinel,
    jira, servicenow, jiraTicketing,
    snowflake,
    qdrant, weaviate, ollama, vllm, kubernetes,
    ingest('sbom', 'SBOM (CI push)', [], ['components']),
    ingest('vuln_scanner', 'Scan file upload', ['vuln_scanning_current', 'vuln_remediation_within_timeframe']),
    ingest('local_upload', 'Manual evidence', []),
    verificationRunner,
    planned('gitlab', 'scm', 'gitlab', 'GitLab', 'Source control'),
    planned('bitbucket', 'scm', 'bitbucket', 'Bitbucket', 'Source control', 'high'),
    planned('terraform_cloud', 'cloud', 'terraform-cloud', 'Terraform Cloud', 'Infrastructure as code', 'high'),
    planned('slack', 'saas', 'slack', 'Slack', 'Collaboration'),
    planned('notion', 'saas', 'notion', 'Notion', 'Collaboration'),
    planned('confluence', 'saas', 'confluence', 'Confluence', 'Collaboration'),
    planned('grafana', 'observability', 'grafana', 'Grafana', 'Observability'),
    planned('newrelic', 'observability', 'newrelic', 'New Relic', 'Observability'),
    // V104-F-3: Jira + ServiceNow ITSM read connectors (const jira / servicenow above, listed in REGISTERED).
];
const BASE_MANIFESTS = Object.freeze([...REGISTERED]);
/** Every registered manifest (a live binding: a test override replaces it). */
// eslint-disable-next-line import/no-mutable-exports
export let CONNECTOR_MANIFESTS = BASE_MANIFESTS;
let BY_KIND = new Map(CONNECTOR_MANIFESTS.map((m) => [m.kind, m]));
registerManifestRegistry(BASE_MANIFESTS, (next) => {
    CONNECTOR_MANIFESTS = next ?? BASE_MANIFESTS;
    BY_KIND = new Map(CONNECTOR_MANIFESTS.map((m) => [m.kind, m]));
});
export function getConnectorManifest(kind) {
    return BY_KIND.get(kind);
}
/** G-1b: the connector kinds that observe a provider (manifest `provider.key`), in registry order. */
export function connectorKindsForProvider(providerKey) {
    return CONNECTOR_MANIFESTS.filter((m) => m.provider.key === providerKey).map((m) => m.kind);
}
/** G-1e: active kinds created on the connections page from their manifest `form`. */
export function formKinds() {
    return CONNECTOR_MANIFESTS.filter((m) => m.lifecycle === 'active' && m.managedBy === 'posture_connectors' && m.form).map((m) => m.kind);
}
/**
 * Kinds a customer can create today. Rows of a `customer_collector` kind are created
 * by the collector ingest on its first accepted push (ADR-0055 §4), never by a create route.
 */
export function creatableKinds() {
    return CONNECTOR_MANIFESTS.filter((m) => m.lifecycle === 'active' && m.family !== 'ingest' && m.managedBy !== 'customer_collector').map((m) => m.kind);
}
/** Kinds with a real pull collector (replaces COLLECTOR_BACKED_KINDS). Uploads and CI push are excluded. */
export function collectorBackedKinds() {
    return CONNECTOR_MANIFESTS.filter((m) => m.lifecycle === 'active' && m.family !== 'ingest').map((m) => m.kind);
}
/** Active kinds a given Worker runtime executes (replaces POSTURE_KINDS / GRANT_KINDS). */
export function kindsForRunner(runner) {
    return CONNECTOR_MANIFESTS.filter((m) => m.lifecycle === 'active' && m.runner === runner).map((m) => m.kind);
}
/** Active kinds whose rows a route family manages. */
export function kindsManagedBy(managedBy) {
    return CONNECTOR_MANIFESTS.filter((m) => m.lifecycle === 'active' && m.managedBy === managedBy).map((m) => m.kind);
}
/** Active kinds that emit vendor-neutral posture checks (the CR26 machine-cadence set). */
export function postureCheckKinds() {
    const keys = new Set(POSTURE_CHECKS.map((c) => c.check_key));
    return CONNECTOR_MANIFESTS
        .filter((m) => m.lifecycle === 'active' && m.runner === 'posture' && m.outputs.checks.some((k) => keys.has(k)))
        .map((m) => m.kind);
}
/** Primary (non-weaker) auth scopes for a kind: what the connector row records. */
export function primaryScopes(kind) {
    const m = BY_KIND.get(kind);
    if (!m)
        return [];
    return (m.auth.find((a) => !a.weaker) ?? m.auth[0])?.scopes ?? [];
}
/**
 * Resolve the control for a check a connector kind emitted: SEC-* from the
 * posture catalog, or an AI control from AI_PROVIDER_CHECKS (V101-3).
 * Undefined when the kind's manifest does not declare the key (ADR-0051:
 * emitters live on the manifest) or the key is in neither catalog.
 */
export function postureControlFor(kind, checkKey) {
    const m = BY_KIND.get(kind);
    if (!m || m.lifecycle !== 'active' || !m.outputs.checks.includes(checkKey))
        return undefined;
    return postureCheck(checkKey)?.control_id ?? aiProviderCheck(checkKey)?.control_id;
}
//# sourceMappingURL=manifests.js.map